import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { REQUIRED_IMPORT_DEFAULT_CATEGORIES } from "constants/qboImportSteps";
import { useToast } from "hooks/useToast";
import { DefaultCategoriesAccordion } from "pages/Books/Settings/DefaultCategoriesAccordion";
import { MultiValue, SingleValue } from "react-select";
import { useGetCOAImportStateQuery } from "store/apis/chartOfAccounts";
import {
  useGetQBOImportDefaultCategoriesQuery,
  useUpdateQBOImportDefaultCategoriesMutation,
} from "store/apis/qbo";
import { ImportCOAData, ImportDefaultCategory } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { getSectionCategoryPrefixes } from "utils/getSectionCategoryPrefixes";
import { HowItWorksModal } from "./HowItWorksModal";
import { ImportQBOHeader, useImportQBO } from "./ImportQBO";

type DefaultCategoriesStepProps = {
  onNext: () => void;
  entityId: string;
  importProcessId: string;
};

type ImportAccountSelectorProps = {
  row: ImportDefaultCategory;
  accounts: ImportCOAData[];
  takenIdentifiers: number[];
  isDisabled: boolean;
  isUpdating: boolean;
  onChange: (row: ImportDefaultCategory, identifier: number) => void;
};

const EMPTY_CATEGORY_DATA: ImportDefaultCategory[] = [];
const EMPTY_ACCOUNTS: ImportCOAData[] = [];

const ImportAccountSelector = ({
  row,
  accounts,
  takenIdentifiers,
  isDisabled,
  isUpdating,
  onChange,
}: ImportAccountSelectorProps) => {
  const prefixes = getSectionCategoryPrefixes(row.section_category_identifiers);
  const selectedIdentifier = row.category?.identifier;

  const options = accounts
    .filter(
      (account) =>
        prefixes.length === 0 || prefixes.includes(account.identifier_prefix)
    )
    .map((account) => ({
      label: account.name,
      value: String(account.identifier),
      isDisabled:
        account.identifier !== selectedIdentifier &&
        takenIdentifiers.includes(account.identifier),
    }));

  const selectedOption =
    options.find((option) => option.value === String(selectedIdentifier)) ??
    null;

  const handleChange = (
    selection: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (selection instanceof Array || !selection?.value) {
      return;
    }

    onChange(row, Number(selection.value));
  };

  return (
    <Combobox
      aria-label={row.title}
      options={options}
      value={selectedOption}
      placeholder="Select"
      size="small"
      menuPortalTarget={document.body}
      onChange={handleChange}
      isClearable={false}
      isDisabled={isDisabled}
      isLoading={isUpdating}
    />
  );
};

export const DefaultCategoriesStep = ({
  onNext,
  entityId,
  importProcessId,
}: DefaultCategoriesStepProps) => {
  const { isOpen, close } = useImportQBO();
  const { alertToast } = useToast();

  const {
    data: categoryData = EMPTY_CATEGORY_DATA,
    ...defaultCategoriesState
  } = useGetQBOImportDefaultCategoriesQuery(
    { entityId },
    { skip: !entityId, refetchOnMountOrArgChange: true }
  );
  const { data: coaImportData, ...coaImportState } = useGetCOAImportStateQuery(
    { entityId, coaImportStateId: importProcessId },
    { skip: !entityId || !importProcessId, refetchOnMountOrArgChange: true }
  );
  const [
    updateDefaultCategories,
    { isLoading: isUpdating, originalArgs: updateArgs },
  ] = useUpdateQBOImportDefaultCategoriesMutation();

  const accounts = coaImportData?.coa_data ?? EMPTY_ACCOUNTS;
  const takenIdentifiers = categoryData.flatMap((row) =>
    row.category ? [row.category.identifier] : []
  );

  const hasMissingRequired = categoryData.some(
    (row) =>
      REQUIRED_IMPORT_DEFAULT_CATEGORIES.includes(row.category_name) &&
      !row.category
  );

  const updatingCategoryName = isUpdating
    ? Object.keys(updateArgs?.defaultCategoryMapping ?? {})[0]
    : null;

  const isNextDisabled =
    hasMissingRequired || isUpdating || categoryData.length === 0;

  const handleAccountChange = async (
    row: ImportDefaultCategory,
    identifier: number
  ) => {
    try {
      await updateDefaultCategories({
        entityId,
        defaultCategoryMapping: { [row.category_name]: identifier },
      }).unwrap();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const renderSelector = (row: ImportDefaultCategory) => (
    <ImportAccountSelector
      row={row}
      accounts={accounts}
      takenIdentifiers={takenIdentifiers}
      isDisabled={isUpdating}
      isUpdating={row.category_name === updatingCategoryName}
      onChange={handleAccountChange}
    />
  );

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={() => close({ takeConfirmation: true })}
      modal={false}
    >
      <Modal.Content size="xl" useCustomOverlay>
        <ImportQBOHeader />
        <Modal.Body className="t-flex t-flex-col t-gap-4 t-overflow-auto t-max-h-[60vh] sm:t-px-4">
          <Async.Root
            isLoading={
              defaultCategoriesState.isLoading || coaImportState.isLoading
            }
            isSuccess={
              defaultCategoriesState.isSuccess && coaImportState.isSuccess
            }
            isError={defaultCategoriesState.isError || coaImportState.isError}
            isEmpty={false}
          >
            <Async.ErrorHandler>
              <Banner
                type="error"
                description={
                  (
                    (defaultCategoriesState.error ||
                      coaImportState.error) as BackendError
                  )?.data?.error?.message ?? "Something went wrong"
                }
              />
            </Async.ErrorHandler>
            <Async.Success>
              <Banner
                type="neutral"
                description="Pick the accounts from your uploaded chart of accounts that Inkle Books should use as defaults. Categories marked * are required to import."
              />
              <DefaultCategoriesAccordion
                categoryData={categoryData}
                renderSelector={renderSelector}
                requiredCategoryNames={REQUIRED_IMPORT_DEFAULT_CATEGORIES}
              />
            </Async.Success>
          </Async.Root>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <HowItWorksModal />
          <Modal.RawClose asChild>
            <Button>Cancel</Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            onClick={onNext}
            disabled={isNextDisabled}
          >
            Next
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
